import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './StudentImport.module.sass';

// Rows whose student ID matches a record with a DIFFERENT name (the import's
// ID-reuse guard, StudentImportRowService#find_student). Left alone, the import
// creates a second record for each -- the source of most new duplicates since
// August (a name changed, or the file's name columns crossed). Ticking "Same
// child" saves at once (PATCH .../same_child) and the import then updates the
// existing record, which takes the file's spelling. Unticked rows import as a
// new record and show up on the school's Possible duplicates page.
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

const SameChildQuestions = ({ rowPreview, studentImportId }) => {
  const conflicts = ((rowPreview && rowPreview.rows) || []).filter((row) => row.id_conflict);
  const [confirmed, setConfirmed] = useState(
    () => new Set(conflicts.filter((row) => row.id_conflict.confirmed).map((row) => row.id_conflict.student_id)),
  );
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!studentImportId || conflicts.length === 0) return null;

  // One save at a time (the boxes are disabled while it is in flight): each save
  // replaces the whole stored list, so overlapping saves could land out of order.
  // The screen then shows what the server stored.
  const toggle = (studentId) => {
    if (saving) return;
    const next = new Set(confirmed);
    if (next.has(studentId)) next.delete(studentId);
    else next.add(studentId);
    setConfirmed(next);
    setSaving(true);

    fetch(`/admin/student_imports/${studentImportId}/same_child`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ student_ids: [...next] }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((data) => {
        setConfirmed(new Set(data.same_child_student_ids));
        setSaveError(null);
      })
      .catch(() => {
        setConfirmed(confirmed);
        setSaveError('That answer was not saved. Tick it again, or reload the page.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className={styles.standaloneSampleList}>
      <strong>{`Same student ID, different name (${conflicts.length})`}</strong>
      <p className={styles.previewNote}>
        These IDs already belong to a record with a different name. Tick the ones that are the same child: the
        existing record is updated to the name in this file. Unticked rows are imported as new records and listed
        as possible duplicates for the school.
      </p>
      {saveError && <p className={styles.previewNote} style={{ color: '#a94442' }}>{saveError}</p>}
      <table className="table table-condensed" style={{ fontSize: '13px', background: '#fff' }}>
        <thead>
          <tr>
            <th>Same child</th>
            <th>Student ID</th>
            <th>Name in this file</th>
            <th>Name on file now</th>
          </tr>
        </thead>
        <tbody>
          {conflicts.map((row) => {
            const studentId = row.id_conflict.student_id;
            return (
              <tr key={row.row_number}>
                <td>
                  <input
                    type="checkbox"
                    checked={confirmed.has(studentId)}
                    disabled={saving}
                    onChange={() => toggle(studentId)}
                    aria-label={`Same child: ${row.first_name} ${row.last_name}`}
                  />
                </td>
                <td>{studentId}</td>
                <td>{`${row.first_name || ''} ${row.last_name || ''}`.trim()}</td>
                <td>{row.id_conflict.name_on_file}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {rowPreview.truncated && (
        <p className={styles.previewNote}>
          Only the first
          {' '}
          {rowPreview.rows.length}
          {' '}
          rows were checked; later rows with this problem are imported as new
          records and listed as possible duplicates.
        </p>
      )}
    </div>
  );
};

SameChildQuestions.propTypes = {
  rowPreview: PropTypes.shape({
    truncated: PropTypes.bool,
    rows: PropTypes.arrayOf(PropTypes.shape({
      row_number: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      id_conflict: PropTypes.shape({
        student_id: PropTypes.string,
        name_on_file: PropTypes.string,
        confirmed: PropTypes.bool,
      }),
    })),
  }),
  studentImportId: PropTypes.number,
};

SameChildQuestions.defaultProps = {
  rowPreview: null,
  studentImportId: null,
};

export default SameChildQuestions;
